import React, { useEffect, useState, useContext } from 'react';
import { Button, FormControl, FormLabel, InputGroup, Row, Col, Modal } from "react-bootstrap";
import ExtrasComponent from "../ExtrasComponent";
import GeofencesMap from "../GeofencesMap";
import ConditionalRender, { Fallback } from "../ConditionalRender";
import MyTable from "../core/Components/MyTable";
import ActionModal from "./ActionModals/ActionModal";
import { patchGeofence } from "../../API/companyApi";
import { useTranslation } from "react-i18next";
import IconButton from "../IconButton";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import css from "../../pages/css/BrowseWorkOrdersPage.module.css";
import EditWorkOrderModal from "./EditWorkOrderModal";
import MapComponente from "../MapComponente";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { SalvoContext } from "../../contexts/SalvoContext";

const EditGeofenceModal = ({ active, geofence, onCancel, onConfirm, onCreateNewWorkOrder, onDeleteWorkOrder, onIntervalsUpdated }) => {

    const { t } = useTranslation()

    const [selectedGeofence, setSelectedGeofence] = useState(geofence)
    const [editPoints, setEditPoints] = useState(false)
    const [selectedGeofenceExtras, setSelectedGeofenceExtras] = useState([])
    const [selectedWorkOrder, setSelectedWorkOrder] = useState(null)
    const [geofenceBeforeEdit, setGeofenceBeforeEdit] = useState(null)
    const [showSpinner, setShowSpinner] = useState(false)
    const { geofenceProjectReference } = useContext(SalvoContext);

    const dateToBeginUtcIso = (dateStr) => (dateStr ? `${dateStr}T00:00:00.000Z` : null);
    const dateToEndUtcIso = (dateStr) => (dateStr ? `${dateStr}T23:59:59.000Z` : null);

    const utcIsoToDateInput = (utcString) => (utcString ? String(utcString).slice(0, 10) : '');

    const openDatePicker = (e) => {
        e.currentTarget.showPicker?.();
    };

    useEffect(() => {
        setSelectedGeofence(geofence)
    }, [geofence]);

    async function updateGeofence() {
        await patchGeofence({ ...selectedGeofence, extras: selectedGeofenceExtras })
        setEditPoints(false)
    }

    const TableHeader = () => {
        return (
            <div className={'d-flex justify-content-between align-items-center'}>
                <h6 className='mb-0'>{t('pages.workOrders.title')}</h6>
                <IconButton icon={faPlus} color="white" bg="#198754" onClick={onCreateNewWorkOrder} />
            </div>
        )
    }

    const WorkOrderRow = ({ workOrder }) => {
        return (
            <tr
                className={css.tableRow}
                onClick={() => setSelectedWorkOrder({
                    ...workOrder,
                    assignedGeofences: workOrder.assignedGeofences.filter(a => a.geofenceId === geofence.id)
                })}
            >
                <td>{workOrder.key}</td>
                <td>{workOrder.name}</td>
            </tr>
        )
    }

    const handleGeofenceClick = (geofence) => {
        setGeofenceBeforeEdit(geofence)
        setEditPoints(true)
    }

    const handleGeofenceCancelEdit = () => {
        setEditPoints(false)
        setSelectedGeofence(geofenceBeforeEdit)
    }

    const handleSave = async () => {
        try {
            setShowSpinner(true);
            await updateGeofence();
            onConfirm?.();
        } finally {
            setShowSpinner(false);
        }
    };

    const handleModalCancel = () => {
        handleGeofenceCancelEdit()
        onCancel()
    }

    const handleCopyGeofenceId = async () => {
        await navigator.clipboard.writeText(selectedGeofence.id)
        toast.success("ID copied to clipboard")
    }

    if (!selectedGeofence)
        return null;

    return (
        <>
            <Modal
                centered
                show={active && !selectedWorkOrder}
                onHide={() => { !showSpinner && onCancel() }}
                size='xl'
                contentClassName={css.modalHeight}
                dialogClassName={css.modalWidth}
            >
                <Modal.Header className='modal-header' >
                    <Modal.Title>{t("pages.workLocations.editWorkLocationDialog.title")}</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ display: 'flex', flexDirection: 'column' }}>
                    <ConditionalRender condition={process.env.REACT_APP_CURRENT_ENV === 'development' || process.env.REACT_APP_CURRENT_ENV === 'staging'}>
                        <FormLabel>Id</FormLabel>
                        <small className={'ms-2 fst-italic'}>Visible in staging only</small>
                        <InputGroup className={'mb-2'}>
                            <FormControl value={selectedGeofence.id} readOnly />
                            <Button variant={'outline-secondary'} onClick={handleCopyGeofenceId}>
                                <FaCopy />
                            </Button>
                        </InputGroup>
                    </ConditionalRender>

                    {/* Name + Project Reference (swapped with Note) */}
                    <Row className="mb-2">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '130px' }} className="mb-0">
                                    {t('dialogs.name')}
                                </FormLabel>
                                <FormControl
                                    placeholder={t('dialogs.nameOptional')}
                                    value={selectedGeofence.name}
                                    onChange={(e) => setSelectedGeofence(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '140px' }} className="mb-0">
                                    {t('pages.workLocations.projectNumber')}
                                </FormLabel>
                                <FormControl
                                    placeholder="1234"
                                    value={selectedGeofence.projectNumber || ''}
                                    onChange={(e) => setSelectedGeofence(prev => ({ ...prev, projectNumber: e.target.value }))}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Type + Note (Note swapped into Project Reference row, long) */}
                    <Row className="mb-2">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '130px' }} className="mb-0">
                                    {t('dialogs.type')}
                                </FormLabel>
                                <FormControl
                                    as="select"
                                    value={selectedGeofence.type || 'customer'}
                                    onChange={(e) => setSelectedGeofence(prev => ({ ...prev, type: e.target.value }))}
                                >
                                    <option value="site">{t('dialogs.site')}</option>
                                    <option value="customer">{t('dialogs.customer')}</option>
                                    <option value="supplier">{t('dialogs.supplier')}</option>
                                    <option value="other">{t('dialogs.other')}</option>
                                </FormControl>
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '140px' }} className="mb-0">
                                    {t('dialogs.note')}
                                </FormLabel>
                                <FormControl
                                    as='textarea'
                                    rows={1}
                                    placeholder={t('dialogs.noteOptional')}
                                    value={selectedGeofence.note}
                                    onChange={(e) => setSelectedGeofence(prev => ({ ...prev, note: e.target.value }))}
                                    style={{ maxHeight: "150px" }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Begin + End Date (date-only, time removed; UTC is fixed values) */}
                    <Row className="mb-2">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '130px' }} className="mb-0">
                                    {t('dialogs.beginTime')}
                                </FormLabel>
                                <FormControl
                                    type="date"
                                    value={utcIsoToDateInput(selectedGeofence.beginUtc)}
                                    onClick={openDatePicker}
                                    style={{ cursor: "pointer" }}
                                    onChange={(e) => setSelectedGeofence(prev => ({
                                        ...prev,
                                        beginUtc: dateToBeginUtcIso(e.target.value)
                                    }))}
                                />
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <FormLabel style={{ minWidth: '140px' }} className="mb-0">
                                    {t('dialogs.endTime')}
                                </FormLabel>
                                <FormControl
                                    type="date"
                                    value={utcIsoToDateInput(selectedGeofence.endUtc)}
                                    onClick={openDatePicker}
                                    style={{ cursor: "pointer" }}
                                    onChange={(e) => setSelectedGeofence(prev => ({
                                        ...prev,
                                        endUtc: dateToEndUtcIso(e.target.value)
                                    }))}
                                />
                            </div>
                        </Col>

                        {(selectedGeofence.beginUtc || selectedGeofence.endUtc) && (
                            <Col xs={12} className="mt-1">
                                <small className="text-muted">{t("dialogs.timeExplanation")}</small>
                            </Col>
                        )}
                    </Row>

                    {geofenceProjectReference ?
                        <ExtrasComponent extras={selectedGeofence.extras} onChange={setSelectedGeofenceExtras} /> : null}
                    <div style={{ height: 10 }} />

                    <ConditionalRender condition={editPoints}>
                        <Fallback>
                            <GeofencesMap
                                geofences={[selectedGeofence]}
                                focusedGeofence={selectedGeofence}
                                editableGeofences={[selectedGeofence]}
                                containerStyle={{ position: 'relative', width: '100%', height: '100%', marginBottom: "20px" }}
                                handleMapClick={() => { }}
                                handleGeofenceClick={handleGeofenceClick}
                                enableFilter={false}
                                useClustering={false}
                            />
                        </Fallback>

                        <MapComponente
                            geofenceToEdit={geofence}
                            containerStyle={{ position: 'relative', width: '100%', height: '100%', marginBottom: "20px" }}
                            onChange={(points) => setSelectedGeofence(prev => ({ ...prev, points }))}
                            onCancelEdit={handleGeofenceCancelEdit}
                        />
                    </ConditionalRender>
                </Modal.Body>

                <Modal.Footer>
                    {showSpinner ? (
                        <LoadingSpinner variant="success" className="mx-4" />
                    ) : (
                        <>
                            <Button variant="danger" onClick={handleModalCancel}>
                                {t('global.operations.cancel')}
                            </Button>

                            <Button
                                variant="success"
                                disabled={selectedGeofence.points.length < 3}
                                onClick={handleSave}
                            >
                                {t('global.operations.save')}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* <EditWorkOrderModal
                active={selectedWorkOrder}
                workOrder={selectedWorkOrder}
                geofenceId={selectedGeofence.id}
                onCancel={() => setSelectedWorkOrder(null)}
                onIntervalsUpdated={onIntervalsUpdated}
            /> */}
        </>
    );
};

export default EditGeofenceModal;
